import _ from 'lodash'
import type {DSConfig} from '../dsConfig'

interface FeatureConfig {
    extensionsList?: string[]
    modulesList?: string[]
    removeExtensionsList?: string[]
    removeModulesList?: string[]
    configOptions?: Partial<Omit<DSConfig, 'extensionsList' | 'modulesList'>>
}

interface OptionalFeatures extends Record<string, FeatureConfig> {}

const featureConfigs: OptionalFeatures = {
    miniSite: {
        extensionsList: ['miniSite'],
        modulesList: ['miniSitePublicAPI']
    },
    optimusMigration: {
        extensionsList: [],
        modulesList: ['optimusMigrationPublicAPI']
    },
    responsiveBreakpointsData: {
        modulesList: ['responsiveBreakpointsDataPublicAPI'],
        extensionsList: ['responsiveBreakpointsData']
    },
    presetsPreviewOnly: {
        configOptions: {
            disableFragments: true
        }
    }
}

const extendWithOptionalFeatures = (config: DSConfig, optionalFeatures: OptionalFeatures, features?: string[]) => {
    if (!features) {
        return config
    }
    const featuresToEnable = _.pick(optionalFeatures, features)
    const modulesToAdd = _(featuresToEnable).flatMap('modulesList').uniq().compact().value()
    const extensionsToAdd = _(featuresToEnable).flatMap('extensionsList').uniq().compact().value()
    const modulesToRemove = _(featuresToEnable).flatMap('removeModulesList').uniq().compact().value()
    const extensionsToRemove = _(featuresToEnable).flatMap('removeExtensionsList').uniq().compact().value()
    const configOptions = _(featuresToEnable)
        .map('configOptions')
        .reduce((acc, options) => ({...acc, ...options}), {})

    const modulesList = _.uniq(_.difference(config.modulesList.concat(modulesToAdd), modulesToRemove))
    const extensionsList = _.uniq(_.difference(config.extensionsList.concat(extensionsToAdd), extensionsToRemove))

    return {
        ...config,
        modulesList,
        extensionsList,
        ...configOptions
    }
}

const extendWithFeatures = (config: DSConfig, features?: string[]) =>
    extendWithOptionalFeatures(config, featureConfigs, features)

interface Experiment {
    isOpen(name: string): boolean
}

const extendWithExperimentalFeatures = (config: DSConfig, experimentInstance: Experiment) => {
    if (!config.experimentalConfigOptions) {
        return config
    }
    const optionalFeatures = config.experimentalConfigOptions
    const runningExperiments = _(config.experimentalConfigOptions)
        .keys()
        .filter(experimentName => experimentInstance.isOpen(experimentName))
        .value()

    return extendWithOptionalFeatures(
        _.omit(config, ['experimentalConfigOptions']),
        optionalFeatures,
        runningExperiments
    )
}
export {extendWithFeatures, extendWithExperimentalFeatures}
